import {Box, Button, Grid, Modal, Stack, Typography} from "@mui/material";
import React, {HTMLAttributes, ReactNode, useEffect, useMemo, useState} from "react";
import {EmployeeRestControllerApiFactory, Holiday, Person, WorkCenter} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {Calendar, dayjsLocalizer, Event, Views} from 'react-big-calendar'
import dayjs from 'dayjs'
import ChairRoundedIcon from "@mui/icons-material/ChairRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import {dedicationModalStyle} from "../modal";
import WikiPageRender from "../../../components/utils/wiki_page";
import {SECTION_HR} from "../../admin/employee/wiki/form";
import {btnSx, EmptyState, FilterChips, Page, PageHeader, Panel} from "../../../components/kit";

const localizer = dayjsLocalizer(dayjs)

// Colores del calendario: los de siempre, con nombre.
const COLOR_CLOSED_DAY = "#ffb0b0";
const COLOR_FULL = "#fdcece";
const COLOR_BELOW_MIN = "#fff2b7";
const COLOR_OPEN = "#c5ffc5";

const Fact = ({label, children}: { label: string, children: ReactNode }) => (
	<Box>
		<Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
		<Typography variant="body2" fontWeight={600} component="div" sx={{overflowWrap: "anywhere"}}>{children}</Typography>
	</Box>
);

/**
 * Reserva de puesto en la oficina (#13877): un centro de trabajo por pestaña, sus datos y condiciones al
 * lado y el calendario del mes. Pulsar un día reserva; el día se pone verde al llegar al mínimo.
 */
export const EmployeeWorkBooking = () => {
	const apiManager = useApiManager();
	const employeeCtrl = apiManager.factory(EmployeeRestControllerApiFactory);
	const [selectedTab, setSelectedTab] = useState<string>("");
	const [workCenters, setWorkCenters] = useState<WorkCenter[]>();
	const [workCenter, setWorkCenter] = useState<WorkCenter | undefined>(undefined);
	const [events, setEvents] = useState<Event[]>([]);
	const [holidays, setHolidays] = useState<Holiday[]>([]);
	const [date, setDate] = useState<Date>(new Date());
	const [showConditionModal, setShowConditionModal] = useState<boolean>(false);

	useEffect(() => {
		const date = new Date();
		// work centers
		apiManager
			.execute(employeeCtrl.getEmployeeWorkCenters())
			.then(centers => {
				setWorkCenters(centers || []);
				if (centers != null && centers.length > 0) {
					setSelectedTab(centers[0].name);
					setWorkCenter(centers[0]);
				}
			});
		// holidays
		apiManager
			.execute(employeeCtrl.getEmployeeHolidays(date.getFullYear()))
			.then(holidays => setHolidays(holidays));

	}, []);

	useEffect(() => {
		if (workCenter) {
			loadBookings(workCenter, date.getMonth() + 1);
		}
	}, [date, workCenter]);

	const {views} = useMemo(
		() => ({
			views: {
				month: true,
			},
		}),
		[]
	)

	const checkIfHoliday = (date: Date): boolean => {
		const m = dayjs(date);
		return holidays.find(h => {
			const m2 = dayjs(h.holidayDate);
			return m2.isSame(m, 'day') && (h.calendar == undefined || h.calendar == workCenter?.calendar);
		}) != undefined;
	}

	const loadBookings = (workCenter: WorkCenter, month: number) => {
		apiManager
			.execute(employeeCtrl.getEmployeeBookings(workCenter.id as number, month))
			.then(bookings => {
				const map = new Map<string, Event>();
				bookings.forEach((elem, i) => {
					let event = map.get(elem.bookingDate);
					if (!event) {
						event = {
							id: elem.id,
							resourceId: elem.id,
							allDay: true,
							start: dayjs(elem.bookingDate).toDate(),
							end: dayjs(elem.bookingDate).add(1, "d").toDate(),
							count: elem.places || 1,
							min: workCenter.minForOpen,
							people: [elem.person],
						} as Event;
						map.set(elem.bookingDate, event);
					} else {
						(event as any).count = (event as any).count + 1;
						(event as any).people.push(elem.person);
					}
				});

				const eventsArr = Array.from(map.values())
					.map(e => {
						const people = (e as any).people.map((p: Person) => p.surnamesAndName).join(",\n");
						return {...e, title: (e as any).count + "/" + workCenter.minForOpen + " por:\n" + people}
					});

				setEvents(eventsArr);
			});
	}

	const center = workCenters?.find(w => w.name === selectedTab);

	return (
		<Page>
			<PageHeader title="Reserva de oficina"
						subtitle="Elige el día en el calendario; la oficina abre cuando se llega al mínimo de personas."/>

			{workCenters && workCenters.length === 0 && (
				<Panel>
					<EmptyState icon={<ChairRoundedIcon/>} title="La reserva de oficina no está disponible ahora mismo."
								description="Todos los centros están configurados como teletrabajo."/>
				</Panel>
			)}

			{workCenters && workCenters.length > 1 && (
				<Box sx={{mb: 2}}>
					<FilterChips value={selectedTab} options={workCenters.map(w => ({value: w.name, label: w.name}))}
								 onChange={name => {
									 setSelectedTab(name);
									 // Al cambiar de centro se cargan sus reservas, no las del anterior.
									 setWorkCenter(workCenters.find(w => w.name === name));
								 }}/>
				</Box>
			)}

			{center && (
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<Stack spacing={3}>
							<Panel title={center.name} icon={<PlaceRoundedIcon fontSize="small" color="action"/>}>
								<Stack spacing={2}>
									<Fact label="Dirección">{center.address}, {center.postalCode}, {center.city}</Fact>
									{center.instructions && <Fact label="Instrucciones">{center.instructions}</Fact>}
									<Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2}}>
										<Fact label="Plazas">{center.places}</Fact>
										<Fact label="Mínimo para abrir">{center.minForOpen}</Fact>
									</Box>
								</Stack>
							</Panel>
							<Panel title="Cómo funciona">
								<Box component="ul" sx={{m: 0, pl: 2.5, "& li": {mb: 0.75, typography: "body2"}}}>
									<li>Pulsa sobre el día que quieras para reservar.</li>
									<li>Se pone en verde cuando se alcanza el mínimo de personas.</li>
									<li>Pulsa sobre una reserva para ver quién más se ha apuntado.</li>
									<li>Para cancelar, avisa a <a href={"mailto:rrhh@arteco-consulting.com"}>RR. HH.</a></li>
								</Box>
								<Stack spacing={0.75} sx={{mt: 2}}>
									{[
										{c: COLOR_OPEN, l: "Abre: mínimo alcanzado"},
										{c: COLOR_BELOW_MIN, l: "Aún no llega al mínimo"},
										{c: COLOR_FULL, l: "Completo"},
										{c: COLOR_CLOSED_DAY, l: "Fin de semana o festivo"},
									].map(x => (
										<Stack key={x.l} direction="row" spacing={1} alignItems="center">
											<Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: x.c, border: "1px solid", borderColor: "divider"}}/>
											<Typography variant="caption" color="text.secondary">{x.l}</Typography>
										</Stack>
									))}
								</Stack>
								<Button size="small" onClick={_ => setShowConditionModal(true)} sx={{...btnSx, mt: 2, ml: -1}}>
									Ver las condiciones de uso
								</Button>
							</Panel>
						</Stack>
					</Grid>
					<Grid item xs={12} md={8}>
						<Panel>
							<Box sx={{height: {xs: 480, md: 600}}}>
								<Calendar
									date={date}
									onNavigate={(date: Date) => {
										setDate(date);
										setWorkCenter(center)
									}}
									localizer={localizer}
									culture="es"
									messages={{today: "Hoy", previous: "Anterior", next: "Siguiente", month: "Mes", week: "Semana", day: "Día", agenda: "Agenda", showMore: n => `+${n} más`}}
									defaultView={Views.MONTH}
									startAccessor="start"
									endAccessor="end"
									views={views}
									events={events}
									selectable={"ignoreEvents"}
									dayPropGetter={(date: Date, resourceId?: number | string): HTMLAttributes<HTMLDivElement> => {
										let color: string | undefined = undefined;
										const isHoliday = checkIfHoliday(date);
										if (date.getDay() == 0 || date.getDay() == 6 || isHoliday) color = COLOR_CLOSED_DAY;
										return {style: {backgroundColor: color}}
									}}
									onSelectEvent={event => {
										alert(event.title);
									}}
									onSelectSlot={slotInfo => {
										const m = dayjs(slotInfo.start);
										const isHoliday = checkIfHoliday(slotInfo.start);
										const strDateApi = m.format("YYYY-MM-DD");
										const strDate = m.format("DD/MM/YYYY");
										if (isHoliday || m.isoWeekday() == 6 || m.isoWeekday() == 7) {
											alert("Este día está marcado como no laboral o festivo");
										} else if (confirm("Deseas aplicar la reserva para el día "
											+ strDate + "?")) {
											apiManager.execute(employeeCtrl.saveEmployeeBooking(center.id as number, strDateApi))
												.then(_ => {
													alert("Se ha creado la reserva. Recuerda que debe haber un mínimo de "
														+ center.minForOpen + " para poder abrir la oficina.");
													loadBookings(center, m.month());
												});

										}
									}}
									eventPropGetter={event => {
										const count = (event as any).count;
										let color: string | undefined = undefined;
										if (count >= center.places) {
											color = COLOR_FULL;
										} else if (count < center.minForOpen) {
											color = COLOR_BELOW_MIN;
										} else {
											color = COLOR_OPEN;
										}
										return {
											style: {
												backgroundColor: color,
												color: "black"
											}
										}
									}}
								/>
							</Box>
						</Panel>
					</Grid>
				</Grid>
			)}

			<Modal
				open={showConditionModal}
				onClose={_ => setShowConditionModal(false)}
				aria-labelledby="condition-modal-title"
				aria-describedby="condition-modal-description">
				<Box sx={dedicationModalStyle}>
					<Typography id="condition-modal-title" variant="h6" component="h2">
						Condiciones de uso de la oficina
					</Typography>
					<Box id="condition-modal-description" sx={{mt: 2}}>
						<WikiPageRender code={"oficina.reserva.condiciones"} section={SECTION_HR}/>
					</Box>
				</Box>
			</Modal>
		</Page>
	);
}
